// Shared types for the eval system (runs, model results, prompt sets).
// See docs/plans/2026-09-11-prompt-eval-system-plan.md §4.

import { SCORED_STAGES, type ExtractionScore, type Stage } from './scoring'

/** A model id the extraction pipeline can run on. The authoritative list is fetched
 *  from the backend (GPT-only) via useAvailableModels; this is a plain string so new
 *  models flow through without a code change. */
export type Model = string

/** GPT-only fallback list, newest → oldest, used when the backend available-models
 *  query is unavailable. Curated from OpenAI's live /v1/models (chat tiers only —
 *  no codex/audio/image/realtime/pro/-chat-latest aliases); the backend fetch returns
 *  whatever is actually available. Last synced 2026-09-11. See useAvailableModels + plan §8. */
export const GPT_MODELS: Model[] = [
  `gpt-5.5`,
  `gpt-5.4`,
  `gpt-5.4-mini`,
  `gpt-5.4-nano`,
  `gpt-5.2`,
  `gpt-5.1`,
  `gpt-5`,
  `gpt-5-mini`,
  `gpt-5-nano`,
  `gpt-4.1`,
  `gpt-4.1-mini`,
  `gpt-4.1-nano`,
  `gpt-4o`,
  `gpt-4o-mini`,
  `gpt-4-turbo`,
  `gpt-4`,
  `gpt-3.5-turbo`,
]

/** The 5 pipeline stages (all run; only 4 are scored — see scoring.ts). */
export const PIPELINE_STAGES = [`overview`, `people`, `appointment`, `outcome`, `validation`] as const
export type PipelineStage = (typeof PIPELINE_STAGES)[number]

/** A candidate/baseline: a prompt version per stage for production's 5-stage pipeline.
 *  With `single` set, the side runs the single-prompt pipeline at that version instead,
 *  and its stage versions go unused. */
export type PromptSet = Record<PipelineStage, number> & { single?: number }

export type TokenUsage = { promptTokens: number; completionTokens: number }

/** How a run executes. `simulated` scores are synthetic (see mockExecutor) and are not
 *  evidence about a prompt. `local-people` runs a real model on the People stage only;
 *  `local-pipeline` runs a whole pipeline on a real model. Both go through the dev-server
 *  model proxy (dev builds only; see opsEvalRunnerPlugin). */
export type ExecutionMode = 'simulated' | 'local-people' | 'local-pipeline'

export const EXECUTION_LABEL: Record<ExecutionMode, string> = {
  simulated: `Simulated scores`,
  'local-people': `Real model · People stage`,
  'local-pipeline': `Real model · full pipeline`,
}

/** The stages each execution mode actually runs, and so the only ones it can score. */
export const EXECUTION_STAGES: Record<ExecutionMode, Stage[]> = {
  simulated: SCORED_STAGES,
  'local-people': [`people`],
  'local-pipeline': SCORED_STAGES,
}

export type RunConfig = {
  datasetId: string
  candidate: PromptSet
  baseline: PromptSet
  models: Model[]
  temperature: number
  /** Defaults to `simulated`. */
  execution?: ExecutionMode
}

/** Per-item, per-model outcome within a run. */
export type ItemResult = {
  itemId: string
  callerName: string
  model: Model
  candidateScore: ExtractionScore
  baselineScore: ExtractionScore
  /** Candidate side's usage, cost and wall time. */
  tokens: TokenUsage
  cost: number
  latencyMs: number
  baseline: { tokens: TokenUsage; cost: number; latencyMs: number }
  /** Free text each side produced (not scored), for review. */
  candidateText?: FreeText
  baselineText?: FreeText
  /** Candidate beat / lost to baseline on this item. */
  improved: boolean
  regressed: boolean
}

export type FreeText = { summary?: string; actionItems?: string }

/** A run's aggregate for one model. */
export type ModelResult = {
  model: Model
  accuracy: number
  baselineAccuracy: number
  delta: number
  stageAccuracy: Record<Stage, number>
  baselineStageAccuracy: Record<Stage, number>
  /** Per-field accuracy across items, keyed `${stage}.${field}`. */
  fieldAccuracy: Record<string, { stage: Stage; field: string; accuracy: number; matched: number; total: number }>
  improvedCount: number
  regressedCount: number
  unchangedCount: number
  totalTokens: number
  cost: number
  baselineCost: number
  items: ItemResult[]
}

export type RunStatus = 'running' | 'complete' | 'failed'

export type Run = {
  id: string
  datasetId: string
  datasetName: string
  candidate: PromptSet
  baseline: PromptSet
  models: Model[]
  temperature: number
  startedAt: string
  finishedAt?: string
  startedBy: string
  status: RunStatus
  execution: ExecutionMode
  /** Stages that count toward this run's scores. */
  stages: Stage[]
  modelResults: ModelResult[]
}

/** A prompt-version activation / release, drawn as a marker on the trend graph. */
export type VersionEvent = {
  date: string
  label: string
  stage?: PipelineStage
}
